import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {FormProvider, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {PasswordField} from "@/components/form/fields/PasswordField/PasswordField.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {GQL_ACCOUNTUSER_PASSWORD_UPDATE} from "@/lib/queries/accounts.ts";
import {GenericMutationResponse, ResponseStatus} from "@/lib/types/common.ts";

interface ChangePasswordFormValues {
  old_password: string;
  new_password1: string;
  new_password2: string;
}

const ChangePasswordFormSchema = Yup.object({
  old_password: Yup.string().required("This field is required."),
  new_password1: Yup.string()
    .required("This field is required.")
    .min(8, "Password must be at least 8 characters.")
    .matches(/^(?=.*[0-9])/, "Password must contain at least one number.")
    .matches(/^(?=.*[a-zA-Z])/, "Password must contain at least one letter.")
    .notOneOf([Yup.ref("old_password")], "New password must be different from old password."),
  new_password2: Yup.string()
    .required("This field is required.")
    .oneOf([Yup.ref("new_password1"), ""], "Confirmation did not match password."),
});

export const UserProfileFormPassword = () => {
  const [updatePassword] = useMutation<GenericMutationResponse>(GQL_ACCOUNTUSER_PASSWORD_UPDATE);

  const methods = useForm<ChangePasswordFormValues>({
    resolver: yupResolver<ChangePasswordFormValues>(ChangePasswordFormSchema),
  });
  const {handleSubmit, reset, setError} = methods;

  const onSubmit = async (data: ChangePasswordFormValues) => {
    const result = await updatePassword({
      variables: {
        data: data,
      },
    });
    const response = result.data?.response;
    if (response?.status === ResponseStatus.SUCCESS) {
      reset();
      toast.success("Password changed successfully.");
    } else if (response?.message) {
      Object.entries(response.message).forEach(([fieldName, messageList]) => {
        setError(fieldName as keyof ChangePasswordFormValues, {message: (messageList as string[])[0]});
      });
    } else {
      toast.error("Something went wrong. Please contact support.");
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, console.error)}>
        <div className="col-xl-6">
          <RequiredFieldsNote />
          <div className="row">
            <PasswordField
              name="old_password"
              label="Current Password"
              placeholder="Current Password"
              autoComplete="current-password"
              required={true}
            />
          </div>
          <div className="row mt-4">
            <PasswordField
              name="new_password1"
              label="New Password"
              placeholder="New Password"
              helpText="Min. 8 characters, at least one letter and number."
              required={true}
            />
          </div>
          <div className="row mt-4">
            <PasswordField
              name="new_password2"
              label="Confirm New Password"
              placeholder="Confirm New Password"
              helpText="Enter the same password as above, for verification."
              required={true}
            />
          </div>

          <Divider className="mt-4" />

          <Button type="submit" className="mt-4">
            Change Password
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
